import React, { useLayoutEffect, useRef, useState } from 'react';
import PrintLayout from '../PrintLayout';
import { BACKTICK_CODES } from '../../../utils/shortcodes';

const CODES = Object.keys(BACKTICK_CODES); // W, I, D, G, E, T in elevation order

// Fixed 1280x720 page (see print.css): footer starts at 650 (720 - 68px footer,
// minus a few px safety margin).
const CONTENT_BOTTOM_PX = 650;
const CONT_HEADER_PX = 60; // continuation pages: just the top padding + title, no heat map/subtitle
const NAME_COL_PX = 92;
const CARD_MARGIN_PX = 8; // QuoteCard's own marginBottom
const FIRST_PAGE_QUOTES_MARGIN_PX = 18; // marginTop on the quotes row, page 1 only
const MEASURE_SAFETY_PX = 4; // small buffer against font-metric rounding

function hexToRgb(hex) {
  const m = hex.replace('#', '').match(/.{2}/g);
  return m.map(h => parseInt(h, 16));
}

// Continuation pages have no heat map, so they hold more per page than page 1 —
// chunk with a smaller first size, then a larger size for every page after.
function chunkVariable(arr, firstSize, restSize) {
  if (!arr.length) return [[]];
  const chunks = [];
  let i = 0;
  let size = firstSize;
  while (i < arr.length) {
    chunks.push(arr.slice(i, i + size));
    i += size;
    size = restSize;
  }
  return chunks;
}

function GeniusHeatMap({ reportData, speakers }) {
  const bySpeaker = reportData.genius?.bySpeaker || {};
  const totals = reportData.genius?.totals || {};
  const maxCount = Math.max(1, ...Object.values(bySpeaker).flatMap(c => Object.values(c)));

  return (
    <div>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: '#6b757e', marginBottom: 8 }}>
        Working Genius Heat Map
      </div>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4, paddingLeft: NAME_COL_PX }}>
        {CODES.map(code => (
          <div key={code} style={{
            flex: 1, textAlign: 'center', marginRight: 6,
            background: BACKTICK_CODES[code].color, color: 'white',
            borderRadius: 4, padding: '3px 0', fontSize: 9, fontWeight: 800,
          }}>
            {BACKTICK_CODES[code].label[0]}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {speakers.map(p => {
          const counts = bySpeaker[p.name] || {};
          return (
            <div key={p.name} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: NAME_COL_PX, fontSize: 9, fontWeight: 600, color: '#2a3d5b', flexShrink: 0, paddingRight: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.name.split('@')[0]}
              </div>
              {CODES.map(code => {
                const count = counts[code] || 0;
                const [r, g, b] = hexToRgb(BACKTICK_CODES[code].color);
                const alpha = count ? 0.15 + 0.75 * (count / maxCount) : 0;
                return (
                  <div key={code} style={{
                    flex: 1, marginRight: 6, padding: '3px 0', borderRadius: 4,
                    textAlign: 'center', fontSize: 8.5, fontWeight: 700,
                    background: count ? `rgba(${r},${g},${b},${alpha})` : '#ececed',
                    color: count ? (alpha > 0.4 ? 'white' : '#2a3d5b') : '#aaa',
                  }}>
                    {count || ''}
                  </div>
                );
              })}
            </div>
          );
        })}

        <div style={{ display: 'flex', alignItems: 'center', paddingTop: 5, marginTop: 2, borderTop: '1px solid rgba(42,61,91,0.12)' }}>
          <div style={{ width: NAME_COL_PX, fontSize: 8.5, fontWeight: 700, color: '#6b757e', flexShrink: 0, paddingRight: 8 }}>
            Total
          </div>
          {CODES.map(code => (
            <div key={code} style={{ flex: 1, marginRight: 6, textAlign: 'center', fontSize: 9, fontWeight: 700, color: '#6b757e' }}>
              {totals[code] || 0}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuoteCard({ q, cardRef }) {
  const color = q.geniusCode && BACKTICK_CODES[q.geniusCode] ? BACKTICK_CODES[q.geniusCode].color : '#ececed';
  return (
    <div ref={cardRef} style={{ marginBottom: CARD_MARGIN_PX, padding: '8px 12px', background: 'white', borderRadius: 6, borderLeft: `3px solid ${color}`, fontSize: 9, color: '#2a3d5b', lineHeight: 1.5 }}>
      <div style={{ fontStyle: 'italic' }}>"{q.text?.slice(0, 110)}{q.text?.length > 110 ? '…' : ''}"</div>
      <div style={{ marginTop: 4, fontSize: 8, color: '#6b757e', fontWeight: 600 }}>{q.speaker?.split('@')[0]}</div>
    </div>
  );
}

export default function PrintQuotesAndMoments({ pageNumber, reportData }) {
  const quotes = reportData.taggedQuotes || [];
  const speakers = (reportData.participation?.speakers || []).filter(s => s.ms > 0).sort((a, b) => b.ms - a.ms);

  // Neither the heat map's height (varies with roster size) nor a quote card's real
  // height (varies with text length/wrapping) can be safely guessed with a fixed px
  // constant, so both are measured off the actual rendered DOM. The first pass renders
  // everything on one page (so nothing is prematurely cut) purely to measure; the
  // layout effect then computes the real budget and re-renders before the browser
  // paints, so what Puppeteer captures already reflects the corrected layout.
  const page1Ref = useRef(null);
  const page1HeaderRef = useRef(null);
  const cardRefs = useRef([]);
  cardRefs.current = [];
  const [metrics, setMetrics] = useState(null); // { available, cardHeight }

  useLayoutEffect(() => {
    if (!page1Ref.current || !page1HeaderRef.current) return;
    const containerRect = page1Ref.current.getBoundingClientRect();
    const headerRect = page1HeaderRef.current.getBoundingClientRect();
    const available = containerRect.bottom - headerRect.bottom - FIRST_PAGE_QUOTES_MARGIN_PX - MEASURE_SAFETY_PX;

    const cardEls = cardRefs.current.filter(Boolean);
    const cardHeight = cardEls.length
      ? Math.max(...cardEls.map(el => el.getBoundingClientRect().height)) + CARD_MARGIN_PX
      : 50;

    setMetrics({ available, cardHeight });
  }, [speakers.length, quotes.length]);

  const cardHeight = metrics?.cardHeight ?? 50;
  // Capacity per column — a page holds two columns, so this is the per-column count.
  const contColumnCap = Math.max(1, Math.floor((CONTENT_BOTTOM_PX - CONT_HEADER_PX) / cardHeight));
  // Before real measurements exist, keep everything on one (unpainted) page so the
  // header block and quote cards render at their natural size for measuring.
  // 0 (not 1) is the correct floor here: if not even one card fits below the
  // heat map, forcing one on anyway just gets it clipped by the page's
  // overflow: hidden — better to leave page 1 empty for quotes and start
  // cleanly on the continuation page.
  const firstColumnCap = metrics
    ? Math.max(0, Math.floor(metrics.available / cardHeight))
    : Math.max(quotes.length, 1);

  const quotePages = chunkVariable(quotes, firstColumnCap * 2, contColumnCap * 2);
  const pageCount = Math.max(quotePages.length, 1);

  return Array.from({ length: pageCount }, (_, pi) => {
    const colCap = pi === 0 ? firstColumnCap : contColumnCap;
    const pageQuotes = quotePages[pi] || [];
    const leftQuotes = pageQuotes.slice(0, colCap);
    const rightQuotes = pageQuotes.slice(colCap);
    let cardIdx = 0;

    return (
      <PrintLayout key={pi} pageNumber={pi === 0 ? pageNumber : null}>
        <div
          ref={pi === 0 ? page1Ref : null}
          style={{ position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, padding: '5% 6%', background: '#f5f4f0', display: 'flex', flexDirection: 'column' }}
        >
          <div ref={pi === 0 ? page1HeaderRef : null}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24, fontWeight: 700, color: '#1e2e45', marginBottom: 4 }}>
              Working Genius by Person{pi > 0 ? ' (cont.)' : ''}
            </div>
            {pi === 0 && (
              <>
                <div style={{ fontSize: 10, color: '#6b757e', marginBottom: 16 }}>Where each person's contributions landed, plus the quotes worth remembering.</div>
                <GeniusHeatMap reportData={reportData} speakers={speakers} />
              </>
            )}
          </div>

          <div style={{ marginTop: pi === 0 ? FIRST_PAGE_QUOTES_MARGIN_PX : 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {pi === 0 && (
              <div style={{ fontSize: 10, fontWeight: 800, color: '#1e2e45', marginBottom: 10, letterSpacing: '0.5px', fontFamily: 'Montserrat, sans-serif' }}>
                Quotes
              </div>
            )}
            {pi === 0 && quotes.length === 0 ? (
              <div style={{ fontSize: 9, color: '#6b757e', fontStyle: 'italic' }}>None recorded</div>
            ) : (
              <div style={{ display: 'flex', gap: 20, flex: 1, minHeight: 0 }}>
                <div style={{ flex: 1 }}>
                  {leftQuotes.map((q) => {
                    const i = cardIdx++;
                    return <QuoteCard key={i} q={q} cardRef={pi === 0 ? (el) => { cardRefs.current[i] = el; } : undefined} />;
                  })}
                </div>
                <div style={{ flex: 1 }}>
                  {rightQuotes.map((q) => {
                    const i = cardIdx++;
                    return <QuoteCard key={i} q={q} cardRef={pi === 0 ? (el) => { cardRefs.current[i] = el; } : undefined} />;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </PrintLayout>
    );
  });
}
